/**
 * Per-table sort state synced to URL (ported from kioskgaming_agent / adminsite).
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  cycleSortState,
  type SortDirection,
  type SortState,
} from '@kioskgaming/ui/dist/data-table/sortState';

export type ColumnValueKind = 'money' | 'datetime' | 'ranked' | 'text';

export interface ColumnSortSpec {
  key: string;
  label: string;
  valueKind: ColumnValueKind;
  magnitude?: boolean;
  defaultDirection?: SortDirection;
}

export interface UseTableSortOptions {
  columns: ColumnSortSpec[];
  paramPrefix?: string;
  onChange?: () => void;
}

export interface UseTableSortResult extends SortState {
  toggleSort: (key: string) => void;
  resetSort: () => void;
  sortParams: { sortBy?: string; sortOrder?: SortDirection };
  isSortable: (key: string) => boolean;
  getColumn: (key: string) => ColumnSortSpec | undefined;
}

function paramNames(prefix?: string) {
  if (!prefix) return { sort: 'sort', dir: 'dir' };
  return { sort: `${prefix}Sort`, dir: `${prefix}Dir` };
}

export function useTableSort({
  columns,
  paramPrefix,
  onChange,
}: UseTableSortOptions): UseTableSortResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const names = useMemo(() => paramNames(paramPrefix), [paramPrefix]);

  const columnMap = useMemo(() => {
    const map = new Map<string, ColumnSortSpec>();
    for (const column of columns) map.set(column.key, column);
    return map;
  }, [columns]);

  const state = useMemo<SortState>(() => {
    const rawKey = searchParams.get(names.sort);
    if (!rawKey || !columnMap.has(rawKey)) {
      return { sortBy: null, sortOrder: null };
    }
    const rawDir = (searchParams.get(names.dir) || '').toLowerCase();
    const fallback = columnMap.get(rawKey)?.defaultDirection ?? 'DESC';
    const sortOrder: SortDirection =
      rawDir === 'asc' ? 'ASC' : rawDir === 'desc' ? 'DESC' : fallback;
    return { sortBy: rawKey, sortOrder };
  }, [searchParams, names, columnMap]);

  const writeState = useCallback(
    (next: SortState) => {
      const params = new URLSearchParams(searchParams);

      if (next.sortBy && next.sortOrder) {
        params.set(names.sort, next.sortBy);
        params.set(names.dir, next.sortOrder.toLowerCase());
      } else {
        params.delete(names.sort);
        params.delete(names.dir);
      }

      params.delete('page');
      setSearchParams(params, { replace: true });
      onChange?.();
    },
    [searchParams, setSearchParams, names, onChange],
  );

  const toggleSort = useCallback(
    (key: string) => {
      const column = columnMap.get(key);
      if (!column) return;
      writeState(cycleSortState(state, key, column.defaultDirection ?? 'DESC'));
    },
    [columnMap, state, writeState],
  );

  const resetSort = useCallback(() => {
    writeState({ sortBy: null, sortOrder: null });
  }, [writeState]);

  const sortParams = useMemo(
    () =>
      state.sortBy && state.sortOrder
        ? { sortBy: state.sortBy, sortOrder: state.sortOrder }
        : {},
    [state],
  );

  const isSortable = useCallback((key: string) => columnMap.has(key), [columnMap]);
  const getColumn = useCallback((key: string) => columnMap.get(key), [columnMap]);

  return {
    ...state,
    toggleSort,
    resetSort,
    sortParams,
    isSortable,
    getColumn,
  };
}

export default useTableSort;
